import { motion } from "framer-motion";
import { Shield, FileText, Lock, Eye, Globe, Mail } from "lucide-react";
import SEO from "../components/SEO";

export default function Privacy() {
    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
            <SEO
                title="Privacy Policy"
                description="Privacy Policy for blinqbooks. Learn how we collect, use, and protect your business data."
            />

            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white dark:bg-gray-800 rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 dark:border-gray-700"
                    >
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                                <Shield className="w-8 h-8" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-primary dark:text-white">Privacy Policy</h1>
                                <p className="text-sm text-secondary dark:text-gray-400">Last updated: December 26, 2025</p>
                            </div>
                        </div>

                        <div className="prose prose-blue dark:prose-invert max-w-none space-y-8 text-secondary dark:text-gray-300 leading-relaxed">
                            <section>
                                <h2 className="text-xl font-bold text-primary dark:text-white mb-4 flex items-center gap-2">
                                    <Globe className="w-5 h-5 text-accent" /> 1. Introduction
                                </h2>
                                <p>
                                    At blinqbooks, your privacy is a priority. This Privacy Policy explains how we collect, use, and protect your information when you use our AI-driven business suite and related services.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-bold text-primary dark:text-white mb-4 flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-accent" /> 2. Information We Collect
                                </h2>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><strong>Account Data:</strong> Information you provide when creating an account, such as name, email, and company details.</li>
                                    <li><strong>Business Data:</strong> Financial records, inventory details, and customer information you upload or connect to the platform.</li>
                                    <li><strong>Usage Data:</strong> Information on how you interact with our platform, collected via logs and analytics tools.</li>
                                    <li><strong>AI Processing Data:</strong> Queries and data points processed by our AI models to provide automation and insights.</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-xl font-bold text-primary dark:text-white mb-4 flex items-center gap-2">
                                    <Lock className="w-5 h-5 text-accent" /> 3. How We Use Your Data
                                </h2>
                                <p>We use your data strictly to provide and improve our services, including:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>Automating accounting and payroll tasks.</li>
                                    <li>Generating business insights and reports.</li>
                                    <li>Ensuring security and preventing fraud.</li>
                                    <li>Providing customer support and technical assistance.</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-xl font-bold text-primary dark:text-white mb-4 flex items-center gap-2">
                                    <Eye className="w-5 h-5 text-accent" /> 4. Data Security
                                </h2>
                                <p>
                                    We implement bank-grade encryption (AES-256) and secure cloud infrastructure to protect your data. Access to your business records is strictly controlled and monitored through advanced audit logs.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-bold text-primary dark:text-white mb-4 flex items-center gap-2">
                                    <Shield className="w-5 h-5 text-accent" /> 5. Your Rights
                                </h2>
                                <p>
                                    As a user, you have the right to access, export, or delete your data at any time. You can also opt-out of optional data collection through your account settings.
                                </p>
                            </section>

                            <section className="p-6 bg-gray-50 dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-700">
                                <h2 className="text-xl font-bold text-primary dark:text-white mb-4 flex items-center gap-2">
                                    <Mail className="w-5 h-5 text-accent" /> Contact Us
                                </h2>
                                <p>
                                    If you have any questions about this Privacy Policy, please contact our legal team at <a href="mailto:privacy@blinqbooks.com" className="text-accent hover:underline">privacy@blinqbooks.com</a>.
                                </p>
                            </section>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
